import React, { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Check, Copy, RefreshCw } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { ContactUpdateRequestDTO } from '@/core/api/dtos/request/location/contactUpdate'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import { PhoneInput } from '@/shared/components/atoms/PhoneNumber'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/shared/components/molecules/AlertDialog'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

export enum ShareLocationModalSteps {
	ContactInfo = 0,
	ShareOptions = 1,
}

interface ShareLocationModalProps {
	visible: boolean
	currentStep: ShareLocationModalSteps
	contactData: ContactUpdateRequestDTO | null
	isUpdating: boolean
	isRegenerating: boolean
	isSharing: boolean
	onClose: () => void
	onNextStep: (data: ContactUpdateRequestDTO) => void
	onRegenerateLink: () => void
	onCopyLink: (url: string) => void
	onShareLink: (url: string) => void
}

const ShareLocationModal: React.FC<ShareLocationModalProps> = ({
	visible,
	currentStep,
	contactData,
	isUpdating,
	isRegenerating,
	isSharing,
	onClose,
	onNextStep,
	onRegenerateLink,
	onCopyLink,
	onShareLink,
}) => {
	const { t } = useTranslation('common')
	const [copied, setCopied] = useState(false)

	// Get the latest location data from Redux (will be used if available)
	const location = useSelector(
		(state: RootState) => state.activeLocation.location
	)

	// Default values for the contact form
	const defaultValues = React.useMemo(
		() => ({
			manager: contactData?.manager || '',
			email: contactData?.email || '',
			phone_number: contactData?.phone_number || '',
		}),
		[contactData]
	)

	// Create validation schema using Yup
	const validationSchema = Yup.object().shape({
		manager: Yup.string().required(t('validation.required')),
		email: Yup.string()
			.email(t('validation.email'))
			.required(t('validation.required')),
		phone_number: Yup.string().required(t('validation.required')),
	})

	const {
		control,
		handleSubmit,
		formState: { errors: _errors },
		reset,
	} = useForm<ContactUpdateRequestDTO>({
		defaultValues,
		resolver: yupResolver(validationSchema) as any,
	})

	const [shareUrl, setShareUrl] = useState('')

	useEffect(() => {
		// Generate share link and QR code value
		// Use origin so we keep the correct protocol + port (e.g. http://localhost:5173)
		if (location?.uuid) {
			setShareUrl(
				`${window.location.origin}/locations/${location.uuid}/details`
			)
		}
	}, [location])

	// Reset form when contactData changes
	useEffect(() => {
		if (contactData) {
			reset({
				manager: contactData.manager || '',
				email: contactData.email || '',
				phone_number: contactData.phone_number || '',
			})
		}
	}, [contactData, reset])

	// Reset form when modal closes
	useEffect(() => {
		if (!visible) {
			reset(defaultValues)
		}
	}, [visible, reset, defaultValues])

	const onSubmit: SubmitHandler<ContactUpdateRequestDTO> = (data) => {
		onNextStep(data)
	}

	const handleOnCopyLink = () => {
		onCopyLink(shareUrl)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	return (
		<Dialog open={visible} onOpenChange={onClose}>
			<DialogContent
				className="sm:max-w-[900px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{/* Header */}
				<div className="px-6 py-6 border-b">
					<DialogTitle className="text-2xl">
						Share location access with the manager via link or QR
					</DialogTitle>
					<p className="text-sm text-muted-foreground mt-2">
						You'll allow the Manager to access location details and volume
						control of zones in real time.
					</p>
				</div>

				{/* Content */}
				{currentStep === ShareLocationModalSteps.ContactInfo ? (
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="flex flex-col flex-1 min-h-0"
					>
						<div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
							<h3 className="text-xl font-semibold">Manager information</h3>

							<div className="space-y-4">
								{/* Manager Name */}
								<div>
									<Label className="mb-2 block">
										Location manager fullname*
									</Label>
									<Controller
										name="manager"
										control={control}
										render={({ field, fieldState: { error } }) => (
											<div>
												<Input
													{...field}
													placeholder="Enter manager name"
													disabled={isUpdating}
													className="h-10 text-sm"
												/>
												{error && <ErrorMessage error={error.message ?? ''} />}
											</div>
										)}
									/>
								</div>

								{/* Email */}
								<div>
									<Label className="mb-2 block">Email*</Label>
									<Controller
										name="email"
										control={control}
										render={({ field, fieldState: { error } }) => (
											<div>
												<Input
													{...field}
													type="email"
													placeholder="Enter email"
													disabled={isUpdating}
													className="h-10 text-sm"
												/>
												{error && <ErrorMessage error={error.message ?? ''} />}
											</div>
										)}
									/>
								</div>

								{/* Phone */}
								<div>
									<Label className="mb-2 block">Phone*</Label>
									<Controller
										name="phone_number"
										control={control}
										render={({ field, fieldState: { error } }) => (
											<div>
												<PhoneInput
													value={field.value}
													onChange={field.onChange}
													placeholder="Enter phone number"
													disabled={isUpdating}
													className="h-10 text-sm"
												/>
												{error && <ErrorMessage error={error.message ?? ''} />}
											</div>
										)}
									/>
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-6 border-t flex justify-end gap-3">
							<Button
								type="button"
								variant="outline"
								onClick={onClose}
								disabled={isUpdating}
								className="rounded-full px-6"
							>
								Cancel
							</Button>
							<Button
								type="submit"
								variant="default"
								disabled={isUpdating}
								className="rounded-full px-6 bg-white text-black hover:bg-gray-100"
							>
								{isUpdating ? 'Loading...' : 'Continue'}
							</Button>
						</div>
					</form>
				) : (
					<div className="flex flex-col flex-1 min-h-0">
						<div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
							<div>
								<h3 className="text-xl font-semibold mb-2">
									QR and link ready to share
								</h3>
								<p className="text-sm text-muted-foreground">
									Here's the link to the location details for the manager. You
									can download the QR code, copy the link, or click "Share" to
									send it via email or WhatsApp.
								</p>
							</div>

							{/* QR Code */}
							<div className="flex justify-center py-6">
								<div className="bg-muted p-6 rounded-lg">
									<QRCodeSVG
										value={shareUrl}
										size={200}
										level="H"
										bgColor="white"
										fgColor="black"
									/>
								</div>
							</div>

							{/* Share Link */}
							<div className="space-y-4">
								<div className="flex items-center gap-2 bg-muted/50 rounded-lg p-4">
									<Input
										value={shareUrl}
										disabled
										className="h-10 text-sm flex-1 bg-transparent border-none"
										readOnly
									/>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										onClick={handleOnCopyLink}
										className="h-10 w-10 flex-shrink-0"
									>
										{copied ? (
											<Check className="h-4 w-4" />
										) : (
											<Copy className="h-4 w-4" />
										)}
									</Button>
								</div>
							</div>

							{/* Regenerate Link Section */}
							<div className="pt-6 border-t space-y-4">
								<h4 className="text-lg font-semibold">Regenerate Link</h4>
								<p className="text-sm text-muted-foreground">
									When you regenerate the link, the one you were using
									previously will no longer work, and you will need to share it
									again with your manager so they can access the location
									details.
								</p>

								<AlertDialog>
									<AlertDialogTrigger asChild>
										<Button
											type="button"
											variant="outline"
											disabled={isRegenerating}
											className="rounded-full px-6"
										>
											<RefreshCw className="h-4 w-4 mr-2" />
											{isRegenerating ? 'Regenerating...' : 'Regenerate link'}
										</Button>
									</AlertDialogTrigger>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>Regenerate link?</AlertDialogTitle>
											<AlertDialogDescription>
												This will invalidate the current link and generate a new
												one. The manager will need the new link to access the
												location.
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel>Cancel</AlertDialogCancel>
											<AlertDialogAction onClick={onRegenerateLink}>
												Confirm
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-6 border-t flex justify-end gap-3">
							<Button
								type="button"
								variant="outline"
								onClick={onClose}
								className="rounded-full px-6"
							>
								Back
							</Button>
							<AlertDialog>
								<AlertDialogTrigger asChild>
									<Button
										type="button"
										variant="default"
										disabled={isSharing}
										className="rounded-full px-6 bg-white text-black hover:bg-gray-100"
									>
										{isSharing ? 'Sharing...' : 'Share'}
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>Share location access?</AlertDialogTitle>
										<AlertDialogDescription>
											This will send the access link to the manager via email.
											They will be able to view location details and control
											zone volumes.
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>Cancel</AlertDialogCancel>
										<AlertDialogAction onClick={() => onShareLink(shareUrl)}>
											Confirm
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}

export default ShareLocationModal
